import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, FormLabel, FormGroup, TextField, Button, FormControl, Box, FilledInput } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { DataGrid } from '@material-ui/data-grid';
import dayjs from 'dayjs';

import Loader from '../../common/loader';
import DateRangeModal from '../../common/date-range';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { get } from '../../utils/api-services';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    searchBtn: {
        alignSelf: 'flex-end',
        marginBottom: 20,
        backgroundColor: '#0d6efd',
        borderColor: '#0d6efd',
        '&:hover': {
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            boxShadow: 'none',
        }
    },
    tableContainer: {
        width: '100%',
        marginBottom: 20
    },
}));

const defProduct = { id: 0, name: 'All products' };
const defType = { id: 0, name: 'All types' };
const defCategory = { id: 0, name: 'All categories' };

const TicketBalance = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const [state, setState] = useState({
        loading: true,
        data: [],
        products: [],
        types: [],
        categories: [],
        filter: {
            product: defProduct,
            type: defType,
            category: defCategory,
            startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
            dateRange: `${dayjs().startOf('month').format('MM/DD/YYYY')} - ${dayjs().format('MM/DD/YYYY')}`,
        },
        rangeModal: {
            open: false,
            range: [
                {
                    startDate: new Date(dayjs().startOf('month').toDate()),
                    endDate: new Date(),
                    key: 'main'
                }
            ]
        },
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'Ticket - Balance';

        makeRequest();
    }, []);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/ticket/indexBalance', state.filter);
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                data: response?.data ?? [],
                products: response.products,
                types: response.types,
                categories: response.categories
            }));
        } else {
            console.log('[TicketBalance] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const search = async () => {
        showLoader(true);
        const response = await get('/ticket/searchBalance', state.filter);
        if (response.status === 200) {
            setState(prev => ({ ...prev, data: response.data }));

            if (!response?.data[0]) showToast('There are no data with these filters', 'warning');
        } else {
            console.log('[TicketBalance] search error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleAutocomplete = (key, value) => setState(prev => ({ ...prev, filter: { ...prev.filter, [key]: value } }));

    const handleDateChange = item => {
        const { startDate, endDate } = item.main;
        const dateRange = `${dayjs(startDate.toISOString()).format('MM/DD/YYYY')} - ${dayjs(endDate.toISOString()).format('MM/DD/YYYY')}`;
        setState(prev => ({
            ...prev,
            filter: {
                ...prev.filter,
                dateRange,
                startDate: dayjs(startDate.toISOString()).format('YYYY-MM-DD'),
                endDate: dayjs(endDate.toISOString()).format('YYYY-MM-DD')
            },
            rangeModal: { ...prev.rangeModal, range: [item.main] }
        }));
    }

    const handleModal = () => setState(prev => ({ ...prev, rangeModal: { ...prev.rangeModal, open: !prev.rangeModal.open } }));

    const valueGetter = ({ value }) => value?.name ?? '?';
    const getDate = ({ row, field }) => row[`${field}Ticket`]?.date ? dayjs(row[`${field}Ticket`].date).format('MM/DD/YYYY') : 'N/A';

    const columns = [
        { field: 'product', headerName: 'Product', flex: 1, minWidth: 180, valueGetter },
        { field: 'type', headerName: 'Type', flex: 1, minWidth: 180, valueGetter },
        { field: 'qty', headerName: 'Pack qty', flex: 1, minWidth: 100 },
        { field: 'first', headerName: 'Start date', flex: 1, minWidth: 100, valueGetter: getDate },
        { field: 'last', headerName: 'Last date', flex: 1, minWidth: 100, valueGetter: getDate },
        { field: 'balance', headerName: 'Balance', flex: 1, minWidth: 100 },
    ]

    if (state.loading) return <Loader />;

    return (
        <div className={classes.root}>
            <h2>{'Balance'}</h2>
            <Grid container>
                <Grid item xs={12} md={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Product'}</FormLabel>

                        <Autocomplete
                            options={state.products}
                            value={state.filter.product}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, product) => handleAutocomplete('product', product)}
                            renderInput={params => <TextField {...params} placeholder='Product' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Type'}</FormLabel>

                        <Autocomplete
                            options={state.types}
                            value={state.filter.type}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, type) => handleAutocomplete('type', type)}
                            renderInput={params => <TextField {...params} placeholder='Type' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Category'}</FormLabel>

                        <Autocomplete
                            options={state.categories}
                            value={state.filter.category}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, type) => handleAutocomplete('category', type)}
                            renderInput={params => <TextField {...params} placeholder='Category' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                    <FormGroup onClick={handleModal}>
                        <FormLabel>{'Date range'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                autoComplete='off'
                                type='text'
                                placeholder='Date range'
                                name='range'
                                value={state.filter.dateRange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

            <Button
                variant='contained'
                className={classes.searchBtn}
                onClick={search}
            >
                {'Search'}
            </Button>

            <div className={classes.tableContainer}>
                <DataGrid
                    autoHeight
                    disableSelectionOnClick
                    pageSize={45}
                    density='compact'
                    rows={state.data}
                    columns={columns}
                    getRowId={row => row.idproduct_type_ref}
                />
            </div>

            <DateRangeModal
                open={state.rangeModal.open}
                range={state.rangeModal.range}
                onClose={handleModal}
                onChange={handleDateChange}
            />
        </div>
    );
}

export default TicketBalance;